import { isNil, isNotNil } from "@etudescarre/utils";
import { type ReactNode, useState } from "react";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import { IfcViewer } from "../ifc-viewer";
import { PdfViewer, type PdfViewerFile } from "../pdf-viewer";
import { ConsignesRecapDialog } from "./consignes-recap-dialog";
import {
  type DestinataireData,
  DestinatairesPopup,
} from "./destinataires-popup";
import { DiffusionDialog, type DiffusionTarget } from "./diffusion-dialog";
import type { DocumentTableProps } from "./document-table";
import { InternalDiffusionPopup } from "./internal-diffusion-popup";
import {
  type SyntheseAvisData,
  SyntheseAvisPopup,
} from "./synthese-avis-popup";
import type { DocumentData, LotData } from "./types";
import { isDocumentVisaEnRetard } from "./visa-retard";

type VersionTarget = {
  open: boolean;
  versionId: number;
  documentId: number;
  documentName: string;
  versionIndex: string | null;
};

const closedTarget: VersionTarget = {
  open: false,
  versionId: 0,
  documentId: 0,
  documentName: "",
  versionIndex: null,
};

function findDocument(
  lots: LotData[],
  matches: (doc: DocumentData) => boolean,
): DocumentData | undefined {
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      const doc = presta.documents.find(matches);
      if (isNotNil(doc)) return doc;
    }
  }
  return undefined;
}

export type DocumentActionsOptions = {
  yardId: string | undefined;
  /** Every document currently displayed, used to name the popups. */
  lots: LotData[];
  /** Sélection du filtre "Documents diffusés à" (ids d'équipes). */
  selectedDiffuseATeamIds?: number[];
  /** Called after a mutation (BPE, diffusion, transmission interne). */
  onChanged: () => void;
};

export type DocumentActions = {
  tableProps: Omit<DocumentTableProps, "lots">;
  /** Popups and viewers; render once, even with several tables. */
  overlays: ReactNode;
};

/**
 * Actions of the "liste des documents" table (viewers, BPE, synthèse,
 * destinataires, diffusion, transmission interne), shared by every screen
 * that offers the same features.
 */
export function useDocumentActions({
  yardId,
  lots,
  selectedDiffuseATeamIds = [],
  onChanged,
}: DocumentActionsOptions): DocumentActions {
  const { contracts, hasContractType } = useYardContext();
  const [pdfFile, setPdfFile] = useState<PdfViewerFile | null>(null);
  const [ifcViewer, setIfcViewer] = useState<{
    fichierIfcId: number;
    title: string;
  } | null>(null);
  const [syntheseData, setSyntheseData] = useState<SyntheseAvisData | null>(
    null,
  );
  const [destinatairesState, setDestinatairesState] = useState<{
    open: boolean;
    documentName: string;
    type: "avis" | "coordination";
    destinataires: DestinataireData[];
  }>({ open: false, documentName: "", type: "avis", destinataires: [] });
  const [diffusionState, setDiffusionState] =
    useState<VersionTarget>(closedTarget);
  const [diffusionLoading, setDiffusionLoading] = useState(false);
  // Grille de transmission interne (formats x equipes du meme contrat)
  const [interneState, setInterneState] = useState<VersionTarget>(closedTarget);
  const [recapState, setRecapState] = useState<VersionTarget>(closedTarget);

  const canMarkBpe = contracts.some(
    (c) => c.type === "travaux_n_1" || c.type === "travaux_n_2",
  );

  const fileUrls = async (fichierId: number) => {
    if (isNil(yardId)) return null;
    const { data } = await apiClient.GET(
      "/api/yards/{yardId}/fichiers/{fichierId}/url",
      { params: { path: { yardId, fichierId: String(fichierId) } } },
    );
    return data?.data ?? null;
  };

  const loadSynthese = async (versionId: number) => {
    if (isNil(yardId)) return null;
    const { data, error } = await apiClient.GET(
      "/api/yards/{yardId}/documents/versions/{versionId}/synthese-avis",
      { params: { path: { yardId, versionId: String(versionId) } } },
    );
    if (isNil(data)) {
      toaster.error({
        title: "Détail de la version non chargé",
        description: error?.error ?? "Réessayez.",
      });
      return null;
    }
    return data.data;
  };

  const versionTarget = (
    versionId: number,
    documentId: number,
  ): VersionTarget => {
    const doc = findDocument(lots, (item) => item.id === documentId);
    return {
      open: true,
      versionId,
      documentId,
      documentName: isNil(doc) ? "" : `${doc.number} - ${doc.name}`,
      versionIndex:
        doc?.versions.find((version) => version.id === versionId)?.index ??
        null,
    };
  };

  const confirmDiffusion = async (
    targets: DiffusionTarget[],
    avisRequis: boolean,
  ) => {
    if (isNil(yardId)) return;
    setDiffusionLoading(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/diffusion",
        {
          params: {
            path: {
              yardId,
              documentId: String(diffusionState.documentId),
              versionId: String(diffusionState.versionId),
            },
          },
          body: { targets, avisRequis },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Document non ajouté à la liste de diffusion",
          description: error.error,
        });
        return;
      }
      toaster.success({ title: "Modifications enregistrées" });
      setDiffusionState((s) => ({ ...s, open: false }));
      onChanged();
    } finally {
      setDiffusionLoading(false);
    }
  };

  const tableProps: DocumentActions["tableProps"] = {
    canMarkBpe,
    selectedDiffuseATeamIds,
    showNoticeFlag:
      hasContractType("travaux_n_1") || hasContractType("travaux_n_2"),
    noticeFlagEditor: isNil(yardId) ? undefined : { yardId, onChanged },
    onOpenRecap: (versionId, documentId) =>
      setRecapState(versionTarget(versionId, documentId)),
    onViewPdf: async (fichierPdfId) => {
      const file = await fileUrls(fichierPdfId);
      if (isNotNil(file)) setPdfFile(file);
    },
    onDownloadNative: async (fichierNativeId) => {
      const file = await fileUrls(fichierNativeId);
      if (isNotNil(file)) window.open(file.url, "_blank");
    },
    onMarkBpe: async (versionId, documentId) => {
      if (isNil(yardId)) return;
      const confirmed = await confirmDialog({
        title: "Marquer BPE",
        message: "Marquer cette version comme BPE ?",
        confirmLabel: "Marquer BPE",
      });
      if (!confirmed) return;
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/bpe",
        {
          params: {
            path: {
              yardId,
              documentId: String(documentId),
              versionId: String(versionId),
            },
          },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Version non marquée BPE",
          description: error.error,
        });
        return;
      }
      toaster.success({ title: "Modifications enregistrées" });
      onChanged();
    },
    onOpenSynthese: async (versionId) => {
      const synthese = await loadSynthese(versionId);
      if (isNil(synthese)) return;
      const doc = findDocument(lots, (item) =>
        item.versions.some((version) => version.id === versionId),
      );
      setSyntheseData({
        ...synthese,
        visaEnRetard: isNotNil(doc)
          ? isDocumentVisaEnRetard(doc, selectedDiffuseATeamIds)
          : false,
        lateTeamIds: doc?.versions.flatMap(
          (version) => version.lateAvisTeamIds ?? [],
        ),
      });
    },
    onOpenDestinataires: async (versionId, type) => {
      const synthese = await loadSynthese(versionId);
      if (isNil(synthese)) return;
      setDestinatairesState({
        open: true,
        documentName: synthese.documentName,
        type,
        destinataires: synthese.destinataires.filter(
          (destinataire) => destinataire.side === type,
        ),
      });
    },
    onOpenInterne: (versionId, documentId) =>
      setInterneState(versionTarget(versionId, documentId)),
    onOpenDiffusion: (versionId, documentId) =>
      setDiffusionState(versionTarget(versionId, documentId)),
    onViewIfc: (fichierIfcId, title) => setIfcViewer({ fichierIfcId, title }),
  };

  const overlays = (
    <>
      {isNotNil(pdfFile) && (
        <PdfViewer file={pdfFile} onClose={() => setPdfFile(null)} />
      )}
      {isNotNil(yardId) && isNotNil(ifcViewer) && (
        <IfcViewer
          yardId={yardId}
          fichierIfcId={ifcViewer.fichierIfcId}
          title={ifcViewer.title}
          onDownload={tableProps.onDownloadNative}
          onClose={() => setIfcViewer(null)}
        />
      )}
      <SyntheseAvisPopup
        open={isNotNil(syntheseData)}
        onClose={() => setSyntheseData(null)}
        data={syntheseData}
        yardId={yardId}
      />
      <DestinatairesPopup
        open={destinatairesState.open}
        onClose={() => setDestinatairesState((s) => ({ ...s, open: false }))}
        documentName={destinatairesState.documentName}
        type={destinatairesState.type}
        destinataires={destinatairesState.destinataires}
      />
      {isNotNil(yardId) && (
        <InternalDiffusionPopup
          open={interneState.open}
          onClose={() => setInterneState((s) => ({ ...s, open: false }))}
          yardId={yardId}
          documentId={interneState.documentId}
          versionId={interneState.versionId}
          documentName={interneState.documentName}
          versionIndex={interneState.versionIndex}
          onTransmitted={onChanged}
        />
      )}
      {isNotNil(yardId) && (
        <ConsignesRecapDialog
          yardId={yardId}
          target={recapState.open ? recapState : null}
          onClose={() => setRecapState(closedTarget)}
          onOpenFile={async (fichierId) => {
            const file = await fileUrls(fichierId);
            if (isNotNil(file)) window.open(file.inlineUrl, "_blank");
          }}
        />
      )}
      <DiffusionDialog
        open={diffusionState.open}
        onClose={() => setDiffusionState((s) => ({ ...s, open: false }))}
        documentName={diffusionState.documentName}
        versionIndex={diffusionState.versionIndex}
        onConfirm={confirmDiffusion}
        loading={diffusionLoading}
      />
    </>
  );

  return { tableProps, overlays };
}
